import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

// --- Types ---

interface Visit {
  id: number;
  property_id: number;
  property_title: string;
  client_name: string | null;
  client_id: number | null;
  agent_name: string | null;
  agent_id: number | null;
  scheduled_at: string;
  status: 'requested' | 'confirmed' | 'completed' | 'cancelled' | 'postponed' | 'no_show';
  notes_client: string | null;
  notes_agent: string | null;
  visit_report: string | null;
  client_interested: boolean | null;
  follow_up_date: string | null;
  cancel_reason: string | null;
  created_at: string;
}

interface VisitsResponse {
  data: Visit[];
  meta?: {
    total: number;
    page: number;
    page_size: number;
  };
}

interface Agent {
  id: number;
  first_name: string;
  last_name: string;
}

interface AgentsResponse {
  data: Agent[];
}

type VisitStatus = Visit['status'];

type ModalType = 'confirm' | 'complete' | 'postpone' | 'cancel' | null;

const STATUS_BADGE: Record<VisitStatus, string> = {
  requested: 'badge-warning',
  confirmed: 'badge-info',
  completed: 'badge-success',
  cancelled: 'badge-error',
  postponed: 'badge-ghost',
  no_show: 'badge-error',
};

// --- Component ---

export default function VisitsPage(): React.ReactElement {
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const isStaff = user?.role === 'company_admin' || user?.role === 'agent';
  const isEndUser = user?.role === 'end_user';

  // State
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);

  // Confirm modal state
  const [confirmAgentId, setConfirmAgentId] = useState<string>('');

  // Complete modal state
  const [completeData, setCompleteData] = useState({
    notes_agent: '',
    visit_report: '',
    client_interested: false,
    follow_up_date: '',
  });

  // Postpone modal state
  const [postponeDate, setPostponeDate] = useState('');

  // Cancel modal state
  const [cancelReason, setCancelReason] = useState('');

  // Toast
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  const modalRef = useRef<HTMLDialogElement>(null);

  // Auto-hide toast
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast((s) => ({ ...s, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Sync modal
  useEffect(() => {
    if (modalType) modalRef.current?.showModal();
    else modalRef.current?.close();
  }, [modalType]);

  // --- Queries ---

  const { data: visitsData, isLoading } = useQuery({
    queryKey: ['visits'],
    queryFn: () => api.get<VisitsResponse>('/v1/visits'),
  });

  const visits = visitsData?.data ?? [];

  // Fetch agents for confirm modal (company staff only)
  const { data: agentsData } = useQuery({
    queryKey: ['company-agents-list'],
    queryFn: () => api.get<AgentsResponse>('/v1/companies/me/agents'),
    enabled: isStaff && modalType === 'confirm',
  });

  const agents = agentsData?.data ?? [];

  // --- Mutations ---

  const confirmMutation = useMutation({
    mutationFn: ({ id, agent_id }: { id: number; agent_id: number }) =>
      api.patch(`/v1/visits/${id}/confirm`, { agent_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      closeModal();
      setToast({
        show: true,
        message: t('visits.confirmSuccess'),
        type: 'success',
      });
    },
    onError: () => {
      setToast({
        show: true,
        message: t('visits.error'),
        type: 'error',
      });
    },
  });

  const completeMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: typeof completeData }) =>
      api.patch(`/v1/visits/${id}/complete`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      closeModal();
      setToast({
        show: true,
        message: t('visits.completeSuccess'),
        type: 'success',
      });
    },
    onError: () => {
      setToast({
        show: true,
        message: t('visits.error'),
        type: 'error',
      });
    },
  });

  const postponeMutation = useMutation({
    mutationFn: ({ id, scheduled_at }: { id: number; scheduled_at: string }) =>
      api.patch(`/v1/visits/${id}/postpone`, { scheduled_at }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      closeModal();
      setToast({
        show: true,
        message: t('visits.postponeSuccess'),
        type: 'success',
      });
    },
    onError: () => {
      setToast({
        show: true,
        message: t('visits.error'),
        type: 'error',
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      api.patch(`/v1/visits/${id}/cancel`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      closeModal();
      setToast({
        show: true,
        message: t('visits.cancelSuccess'),
        type: 'success',
      });
    },
    onError: () => {
      setToast({
        show: true,
        message: t('visits.error'),
        type: 'error',
      });
    },
  });

  // --- Handlers ---

  const openModal = (type: ModalType, visit: Visit): void => {
    setSelectedVisit(visit);
    setModalType(type);
    // Reset modal state
    setConfirmAgentId('');
    setCompleteData({ notes_agent: '', visit_report: '', client_interested: false, follow_up_date: '' });
    setPostponeDate('');
    setCancelReason('');
  };

  const closeModal = (): void => {
    setModalType(null);
    setSelectedVisit(null);
  };

  const handleConfirm = (): void => {
    if (!selectedVisit || !confirmAgentId) return;
    confirmMutation.mutate({ id: selectedVisit.id, agent_id: Number(confirmAgentId) });
  };

  const handleComplete = (): void => {
    if (!selectedVisit) return;
    completeMutation.mutate({
      id: selectedVisit.id,
      data: {
        ...completeData,
        follow_up_date: completeData.follow_up_date || '',
      },
    });
  };

  const handlePostpone = (): void => {
    if (!selectedVisit || !postponeDate) return;
    postponeMutation.mutate({ id: selectedVisit.id, scheduled_at: postponeDate });
  };

  const handleCancel = (): void => {
    if (!selectedVisit) return;
    cancelMutation.mutate({ id: selectedVisit.id, reason: cancelReason.trim() || undefined });
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusLabel = (status: VisitStatus): string => {
    return t(`visits.status.${status}`);
  };

  const isAnyMutationPending =
    confirmMutation.isPending ||
    completeMutation.isPending ||
    postponeMutation.isPending ||
    cancelMutation.isPending;

  // --- Render ---

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">
            {isEndUser
              ? t('visits.myVisitsTitle')
              : t('visits.title')}
          </h1>
          <p className="text-sm text-base-content/60">
            {isEndUser
              ? t('visits.myVisitsSubtitle')
              : t('visits.subtitle')}
          </p>
        </div>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : visits.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-4xl mb-4">&#128197;</p>
          <p className="text-lg text-base-content/60">
            {t('visits.noVisits')}
          </p>
        </div>
      ) : isStaff ? (
        /* Staff view: table */
        <div className="overflow-x-auto bg-base-100 rounded-lg">
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>{t('visits.columns.property')}</th>
                <th>{t('visits.columns.client')}</th>
                <th>{t('visits.columns.agent')}</th>
                <th>{t('visits.columns.date')}</th>
                <th>{t('visits.columns.status')}</th>
                <th>{t('visits.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {visits.map((visit) => (
                <tr key={visit.id}>
                  <td className="font-medium max-w-xs truncate">{visit.property_title}</td>
                  <td>{visit.client_name || '-'}</td>
                  <td>{visit.agent_name || '-'}</td>
                  <td className="whitespace-nowrap">{formatDate(visit.scheduled_at)}</td>
                  <td>
                    <span className={`badge badge-sm ${STATUS_BADGE[visit.status]}`}>
                      {statusLabel(visit.status)}
                    </span>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {/* Requested: Confirm, Cancel */}
                      {visit.status === 'requested' && (
                        <>
                          <button
                            className="btn btn-success btn-xs"
                            onClick={() => openModal('confirm', visit)}
                          >
                            {t('visits.actions.confirm')}
                          </button>
                          <button
                            className="btn btn-error btn-outline btn-xs"
                            onClick={() => openModal('cancel', visit)}
                          >
                            {t('visits.actions.cancel')}
                          </button>
                        </>
                      )}
                      {/* Confirmed: Complete, Postpone, Cancel */}
                      {visit.status === 'confirmed' && (
                        <>
                          <button
                            className="btn btn-success btn-xs"
                            onClick={() => openModal('complete', visit)}
                          >
                            {t('visits.actions.complete')}
                          </button>
                          <button
                            className="btn btn-warning btn-outline btn-xs"
                            onClick={() => openModal('postpone', visit)}
                          >
                            {t('visits.actions.postpone')}
                          </button>
                          <button
                            className="btn btn-error btn-outline btn-xs"
                            onClick={() => openModal('cancel', visit)}
                          >
                            {t('visits.actions.cancel')}
                          </button>
                        </>
                      )}
                      {/* Postponed: Confirm with new date */}
                      {visit.status === 'postponed' && (
                        <button
                          className="btn btn-success btn-xs"
                          onClick={() => openModal('confirm', visit)}
                        >
                          {t('visits.actions.confirm')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* End user view: card list */
        <div className="space-y-3">
          {visits.map((visit) => (
            <div key={visit.id} className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold">{visit.property_title}</h3>
                    <p className="text-sm text-base-content/60 mt-1">
                      {formatDate(visit.scheduled_at)}
                    </p>
                    {visit.agent_name && (
                      <p className="text-sm text-base-content/60">
                        {t('visits.agent')}: {visit.agent_name}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`badge badge-sm ${STATUS_BADGE[visit.status]}`}>
                      {statusLabel(visit.status)}
                    </span>
                    {(visit.status === 'requested' || visit.status === 'confirmed') && (
                      <button
                        className="btn btn-error btn-outline btn-xs"
                        onClick={() => openModal('cancel', visit)}
                      >
                        {t('visits.actions.cancel')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      <dialog ref={modalRef} className="modal" onClose={closeModal}>
        <div className="modal-box">
          {/* Confirm Modal */}
          {modalType === 'confirm' && (
            <>
              <h3 className="font-bold text-lg">
                {t('visits.modals.confirmTitle')}
              </h3>
              {selectedVisit && (
                <p className="text-base-content/70 mt-2">
                  {t('visits.modals.confirmDescription', {
                    property: selectedVisit.property_title,
                  })}
                </p>
              )}
              <div className="form-control w-full mt-4">
                <label className="label">
                  <span className="label-text">
                    {t('visits.modals.selectAgent')} *
                  </span>
                </label>
                <select
                  className="select select-bordered w-full"
                  value={confirmAgentId}
                  onChange={(e) => setConfirmAgentId(e.target.value)}
                >
                  <option value="" disabled>
                    {t('visits.modals.selectAgentPlaceholder')}
                  </option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.first_name} {agent.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-action">
                <button className="btn" onClick={closeModal}>
                  {t('buttons.cancel')}
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleConfirm}
                  disabled={!confirmAgentId || isAnyMutationPending}
                >
                  {confirmMutation.isPending ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    t('visits.actions.confirm')
                  )}
                </button>
              </div>
            </>
          )}

          {/* Complete Modal */}
          {modalType === 'complete' && (
            <>
              <h3 className="font-bold text-lg">
                {t('visits.modals.completeTitle')}
              </h3>
              <div className="space-y-4 mt-4">
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">
                      {t('visits.modals.notesAgent')}
                    </span>
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full"
                    rows={3}
                    placeholder={t('visits.modals.notesAgentPlaceholder')}
                    value={completeData.notes_agent}
                    onChange={(e) => setCompleteData((d) => ({ ...d, notes_agent: e.target.value }))}
                  />
                </div>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">
                      {t('visits.modals.visitReport')}
                    </span>
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full"
                    rows={3}
                    placeholder={t('visits.modals.visitReportPlaceholder')}
                    value={completeData.visit_report}
                    onChange={(e) => setCompleteData((d) => ({ ...d, visit_report: e.target.value }))}
                  />
                </div>
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-primary"
                      checked={completeData.client_interested}
                      onChange={(e) => setCompleteData((d) => ({ ...d, client_interested: e.target.checked }))}
                    />
                    <span className="label-text">
                      {t('visits.modals.clientInterested')}
                    </span>
                  </label>
                </div>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">
                      {t('visits.modals.followUpDate')}
                    </span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    value={completeData.follow_up_date}
                    onChange={(e) => setCompleteData((d) => ({ ...d, follow_up_date: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-action">
                <button className="btn" onClick={closeModal}>
                  {t('buttons.cancel')}
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleComplete}
                  disabled={isAnyMutationPending}
                >
                  {completeMutation.isPending ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    t('visits.actions.complete')
                  )}
                </button>
              </div>
            </>
          )}

          {/* Postpone Modal */}
          {modalType === 'postpone' && (
            <>
              <h3 className="font-bold text-lg">
                {t('visits.modals.postponeTitle')}
              </h3>
              <div className="form-control w-full mt-4">
                <label className="label">
                  <span className="label-text">
                    {t('visits.modals.newDate')} *
                  </span>
                </label>
                <input
                  type="datetime-local"
                  className="input input-bordered w-full"
                  value={postponeDate}
                  onChange={(e) => setPostponeDate(e.target.value)}
                />
              </div>
              <div className="modal-action">
                <button className="btn" onClick={closeModal}>
                  {t('buttons.cancel')}
                </button>
                <button
                  className="btn btn-warning"
                  onClick={handlePostpone}
                  disabled={!postponeDate || isAnyMutationPending}
                >
                  {postponeMutation.isPending ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    t('visits.actions.postpone')
                  )}
                </button>
              </div>
            </>
          )}

          {/* Cancel Modal */}
          {modalType === 'cancel' && (
            <>
              <h3 className="font-bold text-lg">
                {t('visits.modals.cancelTitle')}
              </h3>
              {selectedVisit && (
                <p className="text-base-content/70 mt-2">
                  {t('visits.modals.cancelDescription', {
                    property: selectedVisit.property_title,
                  })}
                </p>
              )}
              <div className="form-control w-full mt-4">
                <label className="label">
                  <span className="label-text">
                    {t('visits.modals.cancelReason')}
                  </span>
                </label>
                <textarea
                  className="textarea textarea-bordered w-full"
                  rows={3}
                  placeholder={t('visits.modals.cancelReasonPlaceholder')}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>
              <div className="modal-action">
                <button className="btn" onClick={closeModal}>
                  {t('buttons.cancel')}
                </button>
                <button
                  className="btn btn-error"
                  onClick={handleCancel}
                  disabled={isAnyMutationPending}
                >
                  {cancelMutation.isPending ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    t('visits.modals.confirmCancel')
                  )}
                </button>
              </div>
            </>
          )}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={closeModal}>close</button>
        </form>
      </dialog>

      {/* Toast */}
      {toast.show && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${toast.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{toast.message}</span>
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => setToast((s) => ({ ...s, show: false }))}
            >
              &#10005;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
